import * as THREE from 'three';

/** Resolves a flora instance id to its current scene object (from the FloraWorld). */
export type ObjectResolver = (id: string) => THREE.Object3D | undefined;

/**
 * Draws a selection highlight (bounding box + ground halo) around selected flora (spec §3.6). It is
 * a pure reflection of {@link SelectionManager} state and holds no selection logic of its own.
 */
export class SelectionRenderer {
  private readonly root = new THREE.Group();
  private readonly highlights = new Map<string, THREE.Object3D>();

  constructor(private readonly resolve: ObjectResolver) {
    this.root.name = 'selection-highlights';
  }

  /** The group of highlight objects — the caller adds this to the scene overlay root. */
  get object(): THREE.Object3D {
    return this.root;
  }

  /** Update highlights to match the selected id set. */
  update(selected: readonly string[]): void {
    const wanted = new Set(selected);

    for (const [id, helper] of this.highlights) {
      if (!wanted.has(id)) {
        this.removeHighlight(id, helper);
      }
    }

    for (const id of wanted) {
      const target = this.resolve(id);
      if (!target) {
        const stale = this.highlights.get(id);
        if (stale) this.removeHighlight(id, stale);
        continue;
      }
      const existing = this.highlights.get(id);
      if (existing instanceof THREE.BoxHelper) {
        existing.setFromObject(target);
        existing.update();
      } else {
        if (existing) this.removeHighlight(id, existing);
        const helper = new THREE.BoxHelper(target, 0xffd54f);
        helper.name = `selection-${id}`;
        this.highlights.set(id, helper);
        this.root.add(helper);
      }
    }
  }

  clear(): void {
    for (const [id, helper] of this.highlights) {
      this.removeHighlight(id, helper);
    }
  }

  dispose(): void {
    this.clear();
    this.root.parent?.remove(this.root);
  }

  private removeHighlight(id: string, helper: THREE.Object3D): void {
    this.root.remove(helper);
    if (helper instanceof THREE.BoxHelper) {
      helper.geometry.dispose();
      (helper.material as THREE.Material).dispose();
    }
    this.highlights.delete(id);
  }
}
